import { useCallback, useRef, useState } from "react";
import { api, type NodeData, type StructureContact } from "../lib/api";
import { normalizeContactType } from "../lib/contacts";

export function useContacts() {
  const contactLookupRef = useRef<Record<string, StructureContact>>({});
  const [contactLookup, setContactLookup] = useState<Record<string, StructureContact>>({});

  const resolveContacts = useCallback(
    (nodeLike: NodeData, lookupOverride?: Record<string, StructureContact>) => {
      const lookup = lookupOverride || contactLookupRef.current || {};
      const contactIds = (nodeLike as any).contact_ids || [];
      const resolvedFromIds = contactIds.map((id: string) => lookup[id]).filter(Boolean);
      const fallbackContacts = (nodeLike.contacts || []).map((c) => ({
        ...c,
        contact_type: c.contact_type || (c as any).contactType || (c as any).type,
      }));
      const base = resolvedFromIds.length > 0 ? resolvedFromIds : fallbackContacts;
      const contactRoles = (nodeLike as any).contact_roles || {};
      return base.map((c) => ({
        ...c,
        contact_type:
          contactRoles[c.id as string] ||
          normalizeContactType((c as any).contact_type || (c as any).contactType || (c as any).type) ||
          (c as any).contact_type ||
          (c as any).type,
      }));
    },
    []
  );

  const loadContactsForStructure = useCallback(async (id: string): Promise<Record<string, StructureContact>> => {
    try {
      const data = await api.listContacts(id, false);
      const map = Object.fromEntries(data.contacts.map((c) => [c.id, c]));
      contactLookupRef.current = map;
      setContactLookup(map);
      return map;
    } catch (err) {
      console.error("[contacts] Failed to load contacts", err);
      contactLookupRef.current = {};
      setContactLookup({});
      return {};
    }
  }, []);

  return { contactLookup, contactLookupRef, setContactLookup, resolveContacts, loadContactsForStructure };
}
